import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { getMcpConsent, submitMcpConsent, MCP_LEGACY_AUTHORIZE_URL, type McpConsent } from '../api/client';
import LanguageSwitcher from '../components/LanguageSwitcher';

export default function McpConnect() {
  const { t, i18n } = useTranslation();
  const { user, isLoading, login, error: loginError } = useAuth();
  const [params] = useSearchParams();
  const continuation = params.get('continuation') || '';
  const userId = user?.id;
  const [consent, setConsent] = useState<McpConsent | null>(null);
  const [tenantId, setTenantId] = useState('');
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'referrer';
    meta.content = 'no-referrer';
    document.head.appendChild(meta);
    return () => { meta.remove(); };
  }, []);

  useEffect(() => {
    setConsent(null);
    setTenantId('');
    setError(false);
    if (!userId || !continuation) return;
    let cancelled = false;
    getMcpConsent(continuation).then((result) => {
      if (cancelled) return;
      setConsent(result);
      if (result.workspaces.length === 1) setTenantId(result.workspaces[0].tenant_id);
    }).catch((err: unknown) => {
      console.error('MCP connection failed', err);
      if (!cancelled) setError(true);
    });
    return () => { cancelled = true; };
  }, [userId, continuation]);

  async function submit(approve: boolean) {
    setBusy(true);
    setError(false);
    try {
      const redirect = await submitMcpConsent(continuation, tenantId, approve);
      window.location.assign(redirect);
    } catch (err) {
      console.error('MCP consent failed', err);
      setError(true);
      setBusy(false);
    }
  }

  return (
    <main dir={i18n.dir()} className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md rounded-xl bg-white p-8 shadow">
        <LanguageSwitcher />
        <h1 className="text-2xl font-bold mt-4">{t('mcpConnect.title')}</h1>
        {!continuation ? <p role="alert">{t('mcpConnect.failed')}</p> : <>
          {isLoading ? <p>{t('common.loading')}</p> : !user ? <>
            <p className="my-4">{t('mcpConnect.signInHelp')}</p>
            <button onClick={login} className="bg-blue-600 text-white rounded px-4 py-2">
              {t('mcpConnect.signIn')}
            </button>
          </> : <>
            <p className="my-4">{t('mcpConnect.identity', { name: user.name })}</p>
            {consent && <>
              <p className="my-4">{t('mcpConnect.access', { client: consent.client_name })}</p>
              {consent.workspaces.length === 0 ? <p role="alert">{t('mcpConnect.noWorkspace')}</p> : <>
                <label htmlFor="mcp-workspace">{t('mcpConnect.workspace')}</label>
                <select id="mcp-workspace" value={tenantId} onChange={(e) => setTenantId(e.target.value)}
                  className="block w-full border rounded p-2 my-3">
                  <option value="">{t('mcpConnect.chooseWorkspace')}</option>
                  {consent.workspaces.map((workspace) =>
                    <option key={workspace.tenant_id} value={workspace.tenant_id}>{workspace.name}</option>)}
                </select>
                <button disabled={busy || !tenantId} onClick={() => submit(true)}
                  className="bg-blue-600 text-white rounded px-4 py-2 disabled:opacity-50">
                  {t('mcpConnect.authorize')}
                </button>
              </>}
              <button disabled={busy} onClick={() => submit(false)} className="mx-3 px-4 py-2">
                {t('common.cancel')}
              </button>
            </>}
          </>}
          {(error || loginError) && <p role="alert" className="text-red-600 mt-4">{error ? t('mcpConnect.failed') : loginError}</p>}
          <details className="mt-6">
            <summary>{t('mcpConnect.legacy')}</summary>
            <p className="my-3">{t('mcpConnect.legacyWarning')}</p>
            <form method="post" action={MCP_LEGACY_AUTHORIZE_URL}>
              <input type="hidden" name="continuation" value={continuation} />
              <label htmlFor="mcp-api-key">{t('mcpConnect.apiKey')}</label>
              <input id="mcp-api-key" name="api_key" type="password" required className="border rounded p-2 w-full" />
              <button type="submit" className="border rounded px-4 py-2 mt-3">{t('mcpConnect.authorize')}</button>
            </form>
          </details>
        </>}
      </div>
    </main>
  );
}
